// src/components/Comparison.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Comparison = () => {
  const { t } = useTranslation();
  // IMPORTANT: Ensure that the translated content for 'comparison.with.items'
  // strictly adheres to the Terms of Service. Benefits listed for "With myPlum+"
  // should focus on providing information, tools for estimation/organization, and guidance.
  // Avoid claims of solving legal issues, guaranteeing outcomes, or replacing legal counsel.
  // All statements must align with the platform's role as an informational resource.
  
  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50 text-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-red-600 opacity-85">{t('comparison.title.without')}</span>
            <span className="mx-3">vs</span>
            <span className="text-green-600">{t('comparison.title.with')}</span>
          </h2>
          <p className="text-xl md:text-2xl font-semibold text-primary">{t('comparison.title.product')}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* Sans myPlum+ */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="group hover:transform hover:scale-101 transition-all duration-300"
          >
            <div className="h-full p-8 bg-white rounded-2xl shadow-lg border-2 border-red-200 hover:border-red-400 transition-colors duration-300">
              <div className="flex items-center justify-center mb-6">
                <h3 className="text-2xl md:text-3xl font-bold text-red-600">{t('comparison.without.title')}</h3>
              </div>
              <ul className="space-y-4 text-left">
                {t('comparison.without.items', { returnObjects: true }).map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="flex-shrink-0 w-5 h-5 mt-1">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                    <span className="text-base md:text-lg">
                      <strong className="font-semibold">{item.title} :</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Avec myPlum+ */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="group hover:transform hover:scale-101 transition-all duration-300"
          >
            <div className="h-full p-8 bg-white rounded-2xl shadow-lg border-2 border-green-200 hover:border-green-400 transition-colors duration-300">
              <div className="flex items-center justify-center mb-6">
                <h3 className="text-2xl md:text-3xl font-bold text-green-600">{t('comparison.with.title')}</h3>
              </div>
              <ul className="space-y-4 text-left">
                {t('comparison.with.items', { returnObjects: true }).map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="flex-shrink-0 w-5 h-5 mt-1">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-base md:text-lg">
                      <strong className="font-semibold">{item.title} :</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
